import { useState } from 'react';
import { estados } from '@/mocks/coverly';

interface Step1Data {
  nombre: string;
  curp: string;
  telefono: string;
  email: string;
  password: string;
  confirmPassword: string;
  estado: string;
  ciudad: string;
  referidoPor: string;
  birthdateFromCurp: string;
}

function extractBirthdateFromCURP(curp: string): Date | null {
  if (curp.length < 10) return null;
  const yy = parseInt(curp.substring(4, 6), 10);
  const mm = parseInt(curp.substring(6, 8), 10);
  const dd = parseInt(curp.substring(8, 10), 10);
  if (isNaN(yy) || isNaN(mm) || isNaN(dd)) return null;
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;
  const fullYear = yy <= 25 ? 2000 + yy : 1900 + yy;
  const date = new Date(fullYear, mm - 1, dd);
  if (date.getMonth() !== mm - 1) return null;
  return date;
}

function calculateAge(birthdate: Date): number {
  const today = new Date();
  let age = today.getFullYear() - birthdate.getFullYear();
  const m = today.getMonth() - birthdate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthdate.getDate())) age--;
  return age;
}

interface RegistroStep1Props {
  onNext: (data: Step1Data) => void;
  onBack: () => void;
  initialReferrerId?: string;
}

export default function RegistroStep1({ onNext, onBack, initialReferrerId = '' }: RegistroStep1Props) {
  const [form, setForm] = useState<Step1Data>({
    nombre: '',
    curp: '',
    telefono: '',
    email: '',
    password: '',
    confirmPassword: '',
    estado: '',
    ciudad: '',
    referidoPor: initialReferrerId,
    birthdateFromCurp: '',
  });
  const [errors, setErrors] = useState<Partial<Step1Data>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const detectedBirthdate = form.curp.length >= 18 ? extractBirthdateFromCURP(form.curp) : null;
  const detectedAge = detectedBirthdate ? calculateAge(detectedBirthdate) : null;
  const isUnderAge = detectedAge !== null && detectedAge < 18;

  const validate = () => {
    const e: Partial<Step1Data> = {};
    if (!form.nombre.trim()) e.nombre = 'Campo requerido';
    if (!form.curp.trim() || form.curp.length < 18) e.curp = 'CURP invalida (18 caracteres)';
    if (!form.telefono.trim()) e.telefono = 'Campo requerido';
    if (!form.email.trim() || !form.email.includes('@')) e.email = 'Email invalido';
    if (form.password.length < 8) e.password = 'Minimo 8 caracteres';
    if (form.confirmPassword !== form.password) e.confirmPassword = 'Las contrasenas no coinciden';
    if (!form.estado) e.estado = 'Selecciona un estado';
    if (!form.ciudad.trim()) e.ciudad = 'Campo requerido';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (isUnderAge) return;
    if (validate()) {
      const birthdateStr = detectedBirthdate
        ? `${detectedBirthdate.getFullYear()}-${String(detectedBirthdate.getMonth() + 1).padStart(2, '0')}-${String(detectedBirthdate.getDate()).padStart(2, '0')}`
        : '';
      onNext({ ...form, birthdateFromCurp: birthdateStr });
    }
  };

  const field = (key: keyof Step1Data, label: string, type = 'text', placeholder = '') => (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
      <input
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={placeholder}
        className="border rounded-xl px-4 py-3 text-sm outline-none transition-all focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20"
        style={{ borderColor: errors[key] ? '#E24B4A' : '#E5E7EB' }}
      />
      {errors[key] && <p className="text-xs text-red-500">{errors[key]}</p>}
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-[#F7F6FF]">
      <div className="flex items-center gap-4 px-4 pt-12 pb-4 bg-white">
        <button onClick={onBack} className="w-8 h-8 flex items-center justify-center cursor-pointer">
          <i className="ri-arrow-left-line text-xl" style={{ color: '#534AB7' }} />
        </button>
        <div className="flex-1 flex justify-center">
          <StepIndicator current={1} />
        </div>
        <div className="w-8" />
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-6">
        <h2 className="text-2xl font-bold text-[#0d0520] mb-1">Cuentanos sobre ti</h2>
        <p className="text-sm text-gray-500 mb-6">
          {initialReferrerId
            ? 'Bienvenido. Estas a punto de protegerte a ti y a tu familia.'
            : 'Necesitamos algunos datos para crear tu membresia'}
        </p>

        {initialReferrerId && (
          <div className="mb-5 rounded-2xl border border-[#DCD8F7] bg-white p-4 shadow-sm">
            <p className="text-xs font-black uppercase tracking-wide text-[#7C7589]">Afiliacion vinculada al ID</p>
            <p className="mt-1 font-mono text-base font-black text-[#534AB7]">{initialReferrerId}</p>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {field('nombre', 'Nombre completo', 'text', 'Ej. Juan Perez Garcia')}

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">CURP</label>
            <input
              type="text"
              value={form.curp}
              onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })}
              placeholder="Ej. PEGJ850101HMCRRN08"
              maxLength={18}
              className="border rounded-xl px-4 py-3 text-sm outline-none transition-all focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20 uppercase"
              style={{ borderColor: errors.curp || isUnderAge ? '#E24B4A' : '#E5E7EB' }}
            />
            {errors.curp && <p className="text-xs text-red-500">{errors.curp}</p>}
            {!errors.curp && detectedBirthdate && !isUnderAge && (
              <p className="text-xs font-medium" style={{ color: '#1D9E75' }}>
                <i className="ri-checkbox-circle-line mr-1" />
                Fecha de nacimiento detectada: {String(detectedBirthdate.getDate()).padStart(2, '0')}/{String(detectedBirthdate.getMonth() + 1).padStart(2, '0')}/{detectedBirthdate.getFullYear()}
              </p>
            )}
            {isUnderAge && (
              <p className="text-xs font-medium text-red-500">
                <i className="ri-error-warning-line mr-1" />
                Debes ser mayor de 18 anos para contratar una membresia Coverly
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Telefono</label>
            <div className="flex items-center border rounded-xl overflow-hidden transition-all focus-within:border-[#534AB7] focus-within:ring-2 focus-within:ring-[#534AB7]/20" style={{ borderColor: errors.telefono ? '#E24B4A' : '#E5E7EB' }}>
              <span className="px-3 py-3 text-sm text-gray-500 bg-gray-50 border-r border-gray-200">+52</span>
              <input
                type="tel"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="55 1234 5678"
                className="flex-1 px-3 py-3 text-sm outline-none bg-white"
              />
            </div>
            {errors.telefono && <p className="text-xs text-red-500">{errors.telefono}</p>}
          </div>

          {field('email', 'Correo electronico', 'email', 'correo@ejemplo.com')}

          <PasswordField
            label="Contrasena"
            value={form.password}
            visible={showPassword}
            error={errors.password}
            placeholder="Minimo 8 caracteres"
            onToggle={() => setShowPassword(!showPassword)}
            onChange={(value) => setForm({ ...form, password: value })}
          />

          <PasswordField
            label="Confirmar contrasena"
            value={form.confirmPassword}
            visible={showConfirmPassword}
            error={errors.confirmPassword}
            placeholder="Repite tu contrasena"
            onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
            onChange={(value) => setForm({ ...form, confirmPassword: value })}
          />
          {!errors.confirmPassword && form.confirmPassword && form.confirmPassword === form.password && (
            <p className="-mt-3 text-xs font-medium text-[#1D9E75]">
              <i className="ri-checkbox-circle-line mr-1" />
              Las contrasenas coinciden
            </p>
          )}

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</label>
            <select
              value={form.estado}
              onChange={(e) => setForm({ ...form, estado: e.target.value })}
              className="border rounded-xl px-4 py-3 text-sm outline-none transition-all focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20 bg-white cursor-pointer"
              style={{ borderColor: errors.estado ? '#E24B4A' : '#E5E7EB' }}
            >
              <option value="">Selecciona tu estado</option>
              {estados.map((est) => (
                <option key={est} value={est}>{est}</option>
              ))}
            </select>
            {errors.estado && <p className="text-xs text-red-500">{errors.estado}</p>}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ciudad / Municipio</label>
            <input
              type="text"
              value={form.ciudad}
              onChange={(e) => setForm({ ...form, ciudad: e.target.value })}
              placeholder="Ej. Los Mochis, Guasave, Mazatlan..."
              className="border rounded-xl px-4 py-3 text-sm outline-none transition-all focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20"
              style={{ borderColor: errors.ciudad ? '#E24B4A' : '#E5E7EB' }}
            />
            {errors.ciudad && <p className="text-xs text-red-500">{errors.ciudad}</p>}
          </div>

          <div className="rounded-2xl border border-[#E3E0FA] bg-white p-4">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Afiliado por</label>
            <input
              type="text"
              value={form.referidoPor}
              onChange={(e) => setForm({ ...form, referidoPor: e.target.value.toUpperCase() })}
              placeholder="ID del miembro que te afilio"
              className="mt-2 w-full rounded-xl border px-4 py-3 font-mono text-sm uppercase outline-none transition-all focus:border-[#534AB7] focus:ring-2 focus:ring-[#534AB7]/20"
              style={{ borderColor: '#E5E7EB' }}
            />
            <p className="mt-2 text-xs leading-5 text-[#7C7589]">
              Si recibiste una invitacion de otro miembro Coverly, coloca aqui su ID para vincular el referido.
            </p>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 bg-white border-t border-gray-100" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom, 16px))' }}>
        <button
          onClick={handleNext}
          disabled={isUnderAge}
          className="w-full py-4 rounded-full text-white font-bold text-base whitespace-nowrap transition-transform active:scale-95"
          style={{ backgroundColor: isUnderAge ? '#C4C0E8' : '#534AB7', cursor: isUnderAge ? 'not-allowed' : 'pointer' }}
        >
          Siguiente →
        </button>
      </div>
    </div>
  );
}

function PasswordField({
  label,
  value,
  visible,
  error,
  placeholder,
  onToggle,
  onChange,
}: {
  label: string;
  value: string;
  visible: boolean;
  error?: string;
  placeholder: string;
  onToggle: () => void;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</label>
      <div className="flex items-center border rounded-xl overflow-hidden bg-white transition-all focus-within:border-[#534AB7] focus-within:ring-2 focus-within:ring-[#534AB7]/20" style={{ borderColor: error ? '#E24B4A' : '#E5E7EB' }}>
        <input
          type={visible ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="min-w-0 flex-1 px-4 py-3 text-sm outline-none"
        />
        <button type="button" onClick={onToggle} className="px-4 text-[#7C7589]">
          <i className={visible ? 'ri-eye-off-line' : 'ri-eye-line'} />
        </button>
      </div>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center gap-2">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300"
            style={{
              backgroundColor: step < current ? '#1D9E75' : step === current ? '#534AB7' : '#E5E7EB',
              color: step <= current ? 'white' : '#9CA3AF',
            }}
          >
            {step < current ? <i className="ri-check-line text-sm" /> : step}
          </div>
          {step < 3 && (
            <div className="w-6 h-0.5 rounded-full" style={{ backgroundColor: step < current ? '#1D9E75' : '#E5E7EB' }} />
          )}
        </div>
      ))}
    </div>
  );
}
